import React, { useState } from 'react';
import { 
  Activity, 
  AlertTriangle, 
  Calculator, 
  Lock, 
  CheckCircle2, 
  ShieldCheck, 
  ArrowRight, 
  Sparkles, 
  Layers,
  FileCheck,
  TrendingUp,
  Clock,
  Check,
  FileDown,
  Monitor
} from 'lucide-react';

interface CoreIntelligenceEnginesProps {
  onOpenDemo: () => void;
}

export const CoreIntelligenceEngines: React.FC<CoreIntelligenceEnginesProps> = ({ onOpenDemo }) => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const engines = [
    {
      id: 'pre-flight-validation',
      title: 'Pre-Flight Shift Validation Engine',
      subtitle: 'Clock-In Hours vs. POS Transactions Cross-Check',
      badge: 'Pre-Approval Guard',
      icon: <AlertTriangle className="text-amber-500" size={24} />,
      color: 'border-amber-500/30 bg-amber-50/30',
      activeColor: 'bg-amber-600 text-white shadow-amber-500/25',
      summary: 'Cross-verifies employee timecard clock-in records against active POS terminal sales activity before managers approve and lock shift allocations.',
      operatorBenefit: 'Catches zero-hour sales, unassigned tip batches, and non-tipped staff role errors before money is distributed.',
      auditOutput: {
        title: 'Pre-Approval Validation Check Log',
        outputType: 'Interactive Review Modal + CSV Shift Ledger Export',
        metric1Label: 'Discrepancies Flagged',
        metric1Val: '1 Resolved (Zero-Hour)',
        metric2Label: 'Allocation Readiness',
        metric2Val: '100% Pre-Validated',
        checks: [
          'Verified: All 12 clocked staff have active shift assignments',
          'State Rule Check: Validated role tip pool eligibility against state guidelines',
          'Flagged & Fixed: Server Alex had $340 in sales with 0.0 hrs clocked (Corrected to 4.5 hrs)',
          'Role Guard: Line Cook John (non-tipped role) blocked from server pool distribution'
        ]
      }
    },
    {
      id: 'yield-variance-flux',
      title: 'Tip Yield Variance & Shift Flux Engine',
      subtitle: 'Labor Hours, Staggered Overlaps & Role Multipliers',
      badge: 'Yield & Shift Variance',
      icon: <Activity className="text-blue-500" size={24} />,
      color: 'border-blue-500/30 bg-blue-50/30',
      activeColor: 'bg-blue-600 text-white shadow-blue-500/25',
      summary: 'Analyzes hourly tip yields across shifts, pro-rates staggered timecard intervals, and applies configurable role multipliers across mixed teams.',
      operatorBenefit: 'Ensures servers working peak dinner rushes receive fair, weighted compensation while flagging abnormal shift yield swings.',
      auditOutput: {
        title: 'Hourly Tip Yield & Trend Report',
        outputType: 'Visual Yield Dashboard + Pre-Payroll Extract File',
        metric1Label: 'Analyzed Labor Hours',
        metric1Val: '32.5 Clocked Hours',
        metric2Label: 'Allocation Balance',
        metric2Val: '100% Reconciled ($0.00 Residual)',
        checks: [
          'Weighted 1.2x Bartender point multipliers applied to 32.5 active hours',
          'State Jurisdictional Check: Applied tenant Business Profile state labor rules',
          'Staggered early-cut shifts pro-rated to exact 15-minute intervals',
          'Automated tip jar cash reconciliation matched to POS credit card totals'
        ]
      }
    },
    {
      id: 'form-8027',
      title: 'Automated IRS Form 8027 Shortfall Engine',
      subtitle: '8% Safe-Harbor True-Up & Shortfall Monitoring',
      badge: 'IRS Tax Compliance',
      icon: <Calculator className="text-emerald-500" size={24} />,
      color: 'border-emerald-500/30 bg-emerald-50/30',
      activeColor: 'bg-emerald-600 text-white shadow-emerald-500/25',
      summary: 'Evaluates gross food & beverage receipts against reported gratuities to ensure IRS Form 8027 8% safe-harbor compliance with automated shortfall calculations.',
      operatorBenefit: 'Supports optional POS sales imports or periodic entry, calculating pro-rata shortfall true-ups to eliminate year-end tax surprises.',
      auditOutput: {
        title: '8% Safe-Harbor True-Up Worksheet',
        outputType: 'Interactive Compliance Screen + Tax Shortfall Export',
        metric1Label: 'Reported Tip Rate (vs. Gross)',
        metric1Val: '9.4% (8.0% Benchmark Met)',
        metric2Label: 'Safe-Harbor Status',
        metric2Val: 'Safe-Harbor Met (+1.4%)',
        checks: [
          'Evaluates gross receipts against total reported gratuities',
          'Supports optional POS imports or manual periodic sales entry',
          'Inspection-ready calculation worksheets and W-2 Box 8 shortfall export schedules'
        ]
      }
    },
    {
      id: 'dispute-lock',
      title: 'Dispute Auto-Escalation & Pre-Payroll Lock',
      subtitle: 'Administrative Hold on Contested Shift Lines',
      badge: 'Zero-Error Payroll',
      icon: <Lock className="text-purple-500" size={24} />,
      color: 'border-purple-500/30 bg-purple-50/30',
      activeColor: 'bg-purple-600 text-white shadow-purple-500/25',
      summary: 'Automatically freezes contested shift lines when an employee flags an issue in their portal, preventing flawed numbers from ever reaching payroll.',
      operatorBenefit: 'Eliminates messy post-payroll paycheck corrections, clawbacks, and group-chat arguments between staff and management.',
      auditOutput: {
        title: 'Pre-Payroll Dispute & Hold Ledger',
        outputType: 'Resolution Management Inbox + Audited Payroll Batch Export',
        metric1Label: 'Open Disputes',
        metric1Val: '0 Pending (1 Resolved)',
        metric2Label: 'Export Status',
        metric2Val: 'Locked & Verified',
        checks: [
          'Dispute #DSP-104 (Shift 08/22 Barback Split) resolved with manager digital note',
          'Automatic administrative lock released after employee digital sign-off',
          'Pre-formatted CSV ready for 1-click import into Gusto, ADP, or Paychex'
        ]
      }
    }
  ];

  const current = engines[activeTab];

  return (
    <section id="features" className="py-24 bg-gray-50/70 relative overflow-hidden scroll-mt-24 border-t border-gray-150">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 border border-blue-100 rounded-full text-[10px] font-black uppercase tracking-widest text-blue-600">
            <Sparkles size={14} className="text-blue-600" />
            Concrete Automated Intelligence
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tighter uppercase italic">
            4 INTELLIGENCE ENGINES <span className="text-blue-600">PROTECTING YOUR VENUE</span>
          </h2>
          <p className="text-gray-500 font-medium max-w-2xl mx-auto text-sm md:text-base">
            Real automated intelligence isn't a buzzword. It's four specialized computational engines actively cross-checking your shifts, hours, taxes, and payroll.
          </p>
        </div>

        {/* Engine Tabs Bar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {engines.map((engine, idx) => (
            <button
              key={engine.id}
              onClick={() => setActiveTab(idx)}
              className={`p-4 rounded-2xl text-left transition-all border flex flex-col justify-between space-y-2 cursor-pointer ${
                activeTab === idx 
                  ? 'bg-gray-900 text-white border-gray-900 shadow-xl scale-[1.02]' 
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between w-full">
                <span className={`p-2 rounded-xl ${activeTab === idx ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>
                  {engine.icon}
                </span>
                <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                  activeTab === idx ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  Engine 0{idx + 1}
                </span>
              </div>
              <div>
                <h3 className={`font-black text-xs md:text-sm uppercase tracking-tight line-clamp-1 ${activeTab === idx ? 'text-white' : 'text-gray-900'}`}>
                  {engine.title}
                </h3>
                <p className={`text-[10px] line-clamp-1 ${activeTab === idx ? 'text-gray-400' : 'text-gray-500'}`}>
                  {engine.subtitle}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Active Engine Showcase Card */}
        <div className="bg-white rounded-[2.5rem] border border-gray-200 p-8 md:p-12 shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          
          {/* Left Description (6 cols) */}
          <div className="lg:col-span-6 space-y-6">
            <div className="space-y-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
                {current.badge}
              </span>
              <h3 className="text-2xl md:text-4xl font-black text-gray-900 uppercase italic tracking-tight">
                {current.title}
              </h3>
              <p className="text-sm md:text-base font-semibold text-gray-700 leading-snug">
                {current.subtitle}
              </p>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed">
              {current.summary}
            </p>

            <div className="p-5 bg-gray-50 rounded-2xl border border-gray-150 space-y-1">
              <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 block">
                Why Operators Rely On It
              </span>
              <p className="text-xs font-bold text-gray-900 leading-relaxed">
                {current.operatorBenefit}
              </p>
            </div>

            <div className="flex items-center gap-4 pt-2">
              <button
                onClick={onOpenDemo}
                className="px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20 active:scale-95 cursor-pointer"
              >
                <span>Launch Interactive Demo</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Right Simulated Engine Output Box (6 cols) */}
          <div className="lg:col-span-6">
            <div className="bg-gray-900 rounded-3xl p-6 md:p-8 text-white space-y-6 shadow-2xl border border-gray-800 relative">
              
              {/* Output Header */}
              <div className="flex items-center justify-between border-b border-gray-800 pb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Live System Output</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-[9px] font-bold">
                  <Monitor size={10} />
                  <span>On-Screen</span>
                  <span className="text-gray-500">•</span>
                  <FileDown size={10} />
                  <span>Export</span>
                </div>
              </div>

              {/* Title & Key Metrics */}
              <div className="space-y-3">
                <div>
                  <h4 className="text-base font-black text-white uppercase italic tracking-tight">
                    {current.auditOutput.title}
                  </h4>
                  <p className="text-[11px] text-blue-400 font-semibold mt-0.5">
                    {current.auditOutput.outputType}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="p-3 bg-white/5 border border-white/10 rounded-xl space-y-1">
                    <span className="text-[9px] font-bold text-gray-400 block uppercase">
                      {current.auditOutput.metric1Label}
                    </span>
                    <span className="text-sm font-black text-white font-mono">
                      {current.auditOutput.metric1Val}
                    </span>
                  </div>
                  <div className="p-3 bg-white/5 border border-white/10 rounded-xl space-y-1">
                    <span className="text-[9px] font-bold text-gray-400 block uppercase">
                      {current.auditOutput.metric2Label}
                    </span>
                    <span className="text-sm font-black text-emerald-400 font-mono">
                      {current.auditOutput.metric2Val}
                    </span>
                  </div>
                </div>
              </div>

              {/* Check Items List */}
              <div className="space-y-2 border-t border-gray-800 pt-4">
                <span className="text-[9px] font-black uppercase tracking-wider text-gray-400 block">
                  Automated Checks Executed:
                </span>
                <div className="space-y-2">
                  {current.auditOutput.checks.map((check, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-300">
                      <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                      <span className="leading-snug">{check}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
